<template>
  <div class="transaction-details">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        variant="text"
        class="mr-2"
        :to="{ name: 'transactions' }"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="text-h4">{{ t('tx.details') }}</h1>
    </div>
    
    <v-skeleton-loader
      v-if="carregando"
      type="card"
    ></v-skeleton-loader>
    
    <template v-else-if="transacao">
      <!-- Status da Transação -->
      <v-card class="mb-4">
        <v-card-text class="d-flex align-center py-4">
          <v-avatar
            :color="transacao.status === 'success' ? 'success' : 'error'"
            class="mr-4"
            size="48"
          >
            <v-icon size="32">
              {{ transacao.status === 'success' ? 'mdi-check' : 'mdi-close' }}
            </v-icon>
          </v-avatar>
          
          <div>
            <div class="text-h6">
              {{ transacao.status === 'success' ? 'Transação Confirmada' : 'Transação Falhou' }}
            </div>
            <div class="text-caption">
              {{ transacao.status === 'success' ? 'Esta transação foi executada com sucesso' : 'Esta transação falhou na execução' }}
            </div>
          </div>
          
          <v-spacer></v-spacer>
          
          <v-btn
            variant="text"
            color="primary"
            :to="{ name: 'block-details', params: { id: transacao.blockNumber } }"
          >
            Ver Bloco #{{ transacao.blockNumber }}
            <v-icon end>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-text>
      </v-card>
      
      <!-- Informações da Transação -->
      <v-card class="mb-4">
        <v-card-title>{{ t('tx.details') }}</v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <InfoItem :label="t('blocks.hash')" :valor="transacao.hash" copiavel />
              <InfoItem label="Bloco" :valor="transacao.blockNumber" />
              <InfoItem label="Hash do Bloco" :valor="transacao.blockHash" copiavel truncar />
              <InfoItem label="Timestamp" :valor="formatarData(transacao.timestamp)" />
              <InfoItem :label="t('tx.status')" :valor="transacao.status === 'success' ? 'Sucesso' : 'Falha'" />
            </v-col>
            
            <v-col cols="12" md="6">
              <InfoItem label="De" :valor="transacao.from" copiavel endereco />
              <InfoItem label="Para" :valor="transacao.to" copiavel endereco />
              <InfoItem :label="t('tx.value')" :valor="formatarValorToken(transacao.value)" destacar />
              <InfoItem :label="t('tx.nonce')" :valor="transacao.nonce" />
              <InfoItem 
                :label="t('tx.fee')" 
                :valor="formatarValorToken((transacao.gasPrice * transacao.gasUsed) / 1e9, 'QRN', 8)" 
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      
      <!-- Detalhes do Gas -->
      <v-card class="mb-4">
        <v-card-title>{{ t('tx.gasDetails') }}</v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <InfoItem label="Gas Utilizado" :valor="formatarNumeroGrande(transacao.gasUsed)" />
              <InfoItem label="Gas Limite" :valor="formatarNumeroGrande(transacao.gas)" />
              <InfoItem 
                label="Utilização de Gas" 
                :valor="`${((transacao.gasUsed / transacao.gas) * 100).toFixed(2)}%`" 
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <InfoItem 
                label="Preço do Gas" 
                :valor="`${(transacao.gasPrice / 1e9).toFixed(2)} q${coin}`" 
              />
              <InfoItem 
                label="Custo de Gas" 
                :valor="formatarValorToken((transacao.gasPrice * transacao.gasUsed) / 1e18, 'QRN', 8)" 
              />
            </v-col>
          </v-row>
          
          <!-- Barra de Progresso de Gas -->
          <v-progress-linear
            class="mt-3"
            :model-value="(transacao.gasUsed / transacao.gas) * 100"
            height="12"
            color="primary"
            striped
            rounded
          ></v-progress-linear>
          
          <div class="d-flex justify-space-between mt-1">
            <span class="text-caption">Gas Utilizado: {{ formatarNumeroGrande(transacao.gasUsed) }}</span>
            <span class="text-caption">Gas Limite: {{ formatarNumeroGrande(transacao.gas) }}</span>
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Input / Dados -->
      <v-card v-if="transacao.input && transacao.input !== '0x'" class="mb-4">
        <v-card-title class="d-flex align-center">
          <span>{{ t('tx.txData') }}</span>
          <v-spacer></v-spacer>
          <v-chip color="info" size="small" class="mr-2">
            {{ formatarBytes(transacao.input.length / 2 - 1) }}
          </v-chip>
          <v-btn-toggle
            v-model="formatoVisualizado"
            density="comfortable"
            color="primary"
            mandatory
          >
            <v-btn value="hex">
              <v-icon small>mdi-code-brackets</v-icon>
              Hex
            </v-btn>
            <v-btn value="utf8">
              <v-icon small>mdi-text</v-icon>
              UTF-8
            </v-btn>
          </v-btn-toggle>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text>
          <v-sheet
            color="grey-darken-4"
            class="pa-4 overflow-auto"
            style="max-height: 200px; border-radius: 4px; font-family: monospace;"
          >
            {{ formatoVisualizado === 'hex' ? transacao.input : hexParaUtf8(transacao.input) }}
          </v-sheet>
          
          <div class="mt-2 text-caption text-grey">
            <v-icon size="small" color="grey">mdi-information-outline</v-icon>
            Os dados de transação podem conter chamadas de função para contratos inteligentes ou mensagens.
          </div>
        </v-card-text>
      </v-card>
      
      <!-- Rastreamento de Eventos -->
      <v-card v-if="transacao.status === 'success'" class="mb-4">
        <v-card-title>
          <span>{{ t('tx.eventsLogs') }}</span>
          <v-spacer></v-spacer>
          <v-chip class="ml-2" size="small">
            {{ (transacao.logs && transacao.logs.length) || 0 }} evento(s)
          </v-chip>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text v-if="!transacao.logs || transacao.logs.length === 0" class="text-center py-8">
          <v-icon size="large" color="grey">mdi-text-box-outline</v-icon>
          <div class="text-h6 grey--text mt-2">{{ t('tx.noEvents') }}</div>
          <div class="text-subtitle-2 grey--text">Esta transação não gerou eventos rastreáveis</div>
        </v-card-text>
        
        <!-- Simulando logs para fins de demonstração (seriam reais em produção) -->
        <v-timeline density="compact" align="start" class="px-4 pb-4">
          <v-timeline-item
            dot-color="primary"
            size="small"
          >
            <template v-slot:opposite>
              <div class="text-caption">0</div>
            </template>
            <div class="d-flex flex-column">
              <div class="text-subtitle-2">{{ t('tx.transfer') }}</div>
              <div class="text-caption text-grey mb-2">
                Evento emitido pelo contrato
                <span class="text-primary">{{ formatarHash('0x' + transacao.to.substring(2, 10) + '00000000000') }}</span>
              </div>
              <v-card variant="outlined" class="pa-2">
                <div class="d-flex align-center mb-1">
                  <div class="text-caption mr-2">from:</div>
                  <div class="text-caption text-primary">{{ formatarHash(transacao.from) }}</div>
                </div>
                <div class="d-flex align-center mb-1">
                  <div class="text-caption mr-2">to:</div>
                  <div class="text-caption text-primary">{{ formatarHash(transacao.to) }}</div>
                </div>
                <div class="d-flex align-center">
                  <div class="text-caption mr-2">value:</div>
                  <div class="text-caption">{{ formatarValorToken(transacao.value, 'QRN', 2) }}</div>
                </div>
              </v-card>
            </div>
          </v-timeline-item>
        </v-timeline>
      </v-card>
      
      <!-- Visualização de Fluxo da Transação -->
      <v-card>
        <v-card-title>{{ t('tx.flow') }}</v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="py-6">
          <div class="transaction-flow">
            <v-sheet class="sender px-4 py-2" color="primary" rounded>
              <div class="text-subtitle-2 text-white">{{ t('tx.sender') }}</div>
              <div class="text-caption text-white mt-1">{{ formatarHash(transacao.from) }}</div>
            </v-sheet>
            
            <div class="flow-arrow">
              <v-icon color="primary" size="large">mdi-arrow-right-bold</v-icon>
              <div class="flow-details">
                <div class="text-caption">{{ formatarValorToken(transacao.value) }}</div>
                <div class="text-caption">Status: {{ transacao.status === 'success' ? 'Sucesso' : 'Falha' }}</div>
              </div>
            </div>
            
            <v-sheet class="receiver px-4 py-2" color="secondary" rounded>
              <div class="text-subtitle-2 text-white">{{ t('tx.recipient') }}</div>
              <div class="text-caption text-white mt-1">{{ formatarHash(transacao.to) }}</div>
            </v-sheet>
          </div>
          
          <div class="text-center mt-6">
            <div class="text-subtitle-1">Confirmada em {{ formatarData(transacao.timestamp) }}</div>
            <div class="text-caption">
              Tempo de finalização: aproximadamente {{ 3 }} segundos
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>
    
    <v-alert
      v-else
      type="error"
      class="mt-4"
    >
      Transação com hash {{ hash }} não encontrada ou ocorreu um erro ao carregar os dados.
    </v-alert>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import { formatarHash, formatarData, formatarBytes, formatarNumeroGrande, formatarValorToken } from '@/services/utilidades'
import InfoItem from '@/components/common/InfoItem.vue'

export default {
  name: 'TransactionDetailsView',
  components: {
    InfoItem
  },
  props: {
    hash: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      carregando: true,
      transacao: null,
      formatoVisualizado: 'hex'
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    }
  },
  methods: {
    formatarHash,
    formatarData,
    formatarBytes,
    formatarNumeroGrande,
    formatarValorToken,
    
    async carregarTransacao() {
      this.carregando = true
      
      try {
        this.transacao = await this.blockchainStore.fetchTransactionByHash(this.hash)
      } catch (error) {
        console.error(`Erro ao carregar transação ${this.hash}:`, error)
        this.transacao = null
      } finally {
        this.carregando = false
      }
    },
    
    hexParaUtf8(hex) {
      if (!hex || !hex.startsWith('0x')) return 'Dados inválidos';
      
      try {
        // Remove o prefixo '0x'
        const hexString = hex.substring(2);
        
        // Converte hex para bytes
        const bytes = new Uint8Array(hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
        
        // Converte bytes para UTF-8
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(bytes);
      } catch (e) {
        console.error('Erro ao converter hex para UTF-8:', e);
        return 'Erro na conversão de dados';
      }
    }
  },
  async mounted() {
    await this.carregarTransacao()
  },
  watch: {
    hash: {
      immediate: true,
      handler() {
        this.carregarTransacao()
      }
    }
  }
}
</script>

<style scoped>
.transaction-details {
  padding-bottom: 32px;
}

.transaction-flow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  margin: 0 20px;
}

.sender, .receiver {
  flex: 0 0 250px;
}

.flow-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;
}

.flow-details {
  text-align: center;
  margin-top: 8px;
}
</style>
